import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from 'react-i18next';

// Auth context oluştur
const AuthContext = createContext();

// Auth provider bileşeni
export const AuthProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Oturum durumu değişikliklerini izle
  useEffect(() => {
    // LocalStorage'dan kullanıcı bilgilerini kontrol et
    const session = localStorage.getItem('alasfireSession');
    if (session) {
      const sessionData = JSON.parse(session);
      setUser(sessionData.user);
      setDealer(sessionData.dealer);
    }
    
    setLoading(false);
    
    // Oturum değişikliği dinleyici
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setDealer(null);
        localStorage.removeItem('alasfireSession');
      }
    });
    
    return () => {
      if (authListener) authListener.subscription.unsubscribe();
    };
  }, []);

  // Giriş işlemi
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // Test kullanıcısı kontrolü
      if (username === 'testbayi' && password === 'test123') {
        // Test kullanıcısı için oturum oluştur
        const testSessionData = {
          user: {
            id: '00000000-0000-0000-0000-000000000001',
            email: 'test@alasfire.com'
          },
          dealer: {
            id: '00000000-0000-0000-0000-000000000001',
            username: 'testbayi',
            dealer_name: 'Test Bayisi'
          },
          token: 'test-token-123'
        };
        
        // State güncelle
        setUser(testSessionData.user);
        setDealer(testSessionData.dealer);
        
        // LocalStorage'a kaydet
        localStorage.setItem('alasfireSession', JSON.stringify(testSessionData));
        
        return { success: true };
      }
      
      // Gerçek Supabase kimlik doğrulama
      try {
        // Kullanıcı adını e-posta olarak formatla (kullanıcı adını doğrudan kullan)
        // Not: Bu format, username@alasfire.com şeklinde olmalı - sadece "username" olarak kullanmak hataya neden olur
        const email = `${username}@alasfire.com`;
        
        // Supabase ile giriş yap
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (error) {
          console.error('Supabase giriş hatası:', error);
          
          // Hata türüne göre çevirilmiş mesajları döndür
          if (error.message.includes('Invalid login credentials')) {
            throw new Error(t('login.errors.invalid_credentials'));
          } else if (error.message.includes('Failed to fetch')) {
            throw new Error(t('login.errors.connection_failed'));
          } else {
            throw new Error(t('login.errors.login_failed'));
          }
        }
        
        if (!data || !data.session) {
          throw new Error(t('login.errors.invalid_response'));
        }
        
        // Kullanıcı bilgilerini alma
        const { data: userData, error: userError } = await supabase
          .from('dealers')
          .select('*')
          .eq('username', username)
          .single();
        
        if (userError) {
          console.error('Kullanıcı bilgileri hatası:', userError);
          throw new Error(t('login.errors.server_error'));
        }
        
        // Oturum bilgilerini oluştur
        const sessionData = {
          user: data.user,
          dealer: userData,
          token: data.session.access_token
        };
        
        // State güncelle
        setUser(data.user);
        setDealer(userData);
        
        // LocalStorage'a kaydet
        localStorage.setItem('alasfireSession', JSON.stringify(sessionData));
        
        return { success: true };
      } catch (error) {
        console.error('Supabase giriş hatası:', error);
        throw error;
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Çıkış işlemi
  const logout = async () => {
    setLoading(true);
    
    try {
      await supabase.auth.signOut();
      setUser(null);
      setDealer(null);
      localStorage.removeItem('alasfireSession');
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Auth durumunu kontrol et
  const checkAuth = () => {
    const session = localStorage.getItem('alasfireSession');
    return !!session;
  };

  // Context değerlerini sağla
  const value = {
    user,
    dealer,
    loading,
    error,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Auth context hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 